import { DialogV2, Typography } from '@recruit-robin/robin-theme/web-components';
import {
  ArrowNarrowUpRight,
  CheckCircleFilled,
  Mail_01,
  RefreshCw_02,
  RobinIconMonoFulfilled,
  Send_01,
  XCircle,
} from '@recruit-robin/robin-theme/web-icons';
import { useCallback } from 'react';

import { Indeed } from 'shared/legacy/icons/Indeed/Indeed';
import { resolveIndeedRjSendErrorMessage } from 'shared/legacy/exceptions/resolveIndeedRjSendErrorMessage';
import { useDictionary } from '@/shared/lib/i18n';
import type { BulkSendSlot } from '../../api';

import { BulkProgressBar } from '../bulk-progress-bar';
import { useStyles } from './BulkSendingProgress.styles';
import type { BulkSendingProgressProps } from './BulkSendingProgress.types';

const INDEED_SESSION_LINK = 'https://employers.indeed.com/';

function SendSlotRow({ slot, onRetry }: { slot: BulkSendSlot; onRetry?: (esPersonId: string) => void }) {
  const classes = useStyles();
  const dictionary = useDictionary();

  const handleRetry = useCallback(() => {
    onRetry?.(slot.esPersonId);
  }, [slot.esPersonId, onRetry]);

  const handleOpenIndeedSession = useCallback(() => {
    window.open(INDEED_SESSION_LINK, '_blank', 'noopener');
  }, []);

  if (slot.status === 'success' && slot.flow === 'indeed') {
    return (
      <div css={classes.row}>
        <div css={classes.rowLeft}>
          <div css={classes.iconContainer}>
            <Indeed width={16} height={16} />
          </div>
          <div>
            <Typography
              variant={(v) => v.heading[5]}
              color={(c) => c.neutral.variant[80]}
              customCss={classes.candidateName}>
              {slot.candidateName}
            </Typography>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[60]}>
              {dictionary.bulkIndeedWaitingForReply}
            </Typography>
          </div>
        </div>
        <div css={classes.successIcon}>
          <CheckCircleFilled size={20} color={(c) => c.tertiary.lime[30]} />
        </div>
      </div>
    );
  }

  if (slot.status === 'success') {
    return (
      <div css={classes.row}>
        <div css={classes.rowLeft}>
          <div css={classes.iconContainer}>
            <Mail_01 size={16} color={(c) => c.neutral.variant[50]} />
          </div>
          <Typography
            variant={(v) => v.heading[5]}
            color={(c) => c.neutral.variant[80]}
            customCss={classes.candidateName}>
            {slot.candidateName}
          </Typography>
        </div>
        <div css={classes.successIcon}>
          <CheckCircleFilled size={20} color={(c) => c.secondary.emerald[50]} />
        </div>
      </div>
    );
  }

  if (slot.status === 'error' && slot.flow === 'indeed') {
    const causeMessage = resolveIndeedRjSendErrorMessage(slot.error, dictionary);
    const isGenericFailure = causeMessage === dictionary.somethingWentWrong;

    return (
      <div css={classes.row}>
        <div css={classes.rowLeft}>
          <div css={classes.iconContainer}>
            <XCircle size={16} color={(c) => c.tertiary.red[50]} />
          </div>
          <div>
            <Typography
              variant={(v) => v.heading[5]}
              color={(c) => c.tertiary.red[60]}
              customCss={classes.candidateName}>
              {dictionary.bulkIndeedSendingFailure(slot.candidateName)}
            </Typography>
            <Typography variant={(v) => v.body.short} color={(c) => c.tertiary.red[50]}>
              {causeMessage}
            </Typography>
            {isGenericFailure && (
              <Typography variant={(v) => v.body.short} color={(c) => c.tertiary.red[50]}>
                {dictionary.bulkIndeedCheckSession}
              </Typography>
            )}
          </div>
        </div>
        {isGenericFailure && (
          <div css={classes.sessionLinkButton} onClick={handleOpenIndeedSession}>
            <ArrowNarrowUpRight size={16} color={(c) => c.neutral.variant[50]} />
          </div>
        )}
        {onRetry && (
          <div css={classes.retryButton} onClick={handleRetry}>
            <RefreshCw_02 size={16} color={(c) => c.primary.fuchsia[50]} />
          </div>
        )}
      </div>
    );
  }

  if (slot.status === 'error') {
    return (
      <div css={classes.row}>
        <div css={classes.rowLeft}>
          <div css={classes.iconContainer}>
            <XCircle size={16} color={(c) => c.tertiary.red[50]} />
          </div>
          <Typography variant={(v) => v.heading[5]} color={(c) => c.tertiary.red[60]} customCss={classes.candidateName}>
            {dictionary.bulkSendingFailure(slot.candidateName)}
          </Typography>
        </div>
        {onRetry && (
          <div css={classes.retryButton} onClick={handleRetry}>
            <RefreshCw_02 size={16} color={(c) => c.primary.fuchsia[50]} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div css={classes.row}>
      <div css={classes.rowLeft}>
        <div css={classes.iconContainer}>
          <RobinIconMonoFulfilled size={16} color={(c) => c.primary.fuchsia[40]} />
        </div>
        <Typography
          variant={(v) => v.heading[5]}
          color={(c) => c.primary.fuchsia[40]}
          customCss={classes.candidateName}>
          {dictionary.bulkSendingReachingOut(slot.candidateName)}
        </Typography>
      </div>
    </div>
  );
}

export function BulkSendingProgress({ slots, onClose, onRetry }: BulkSendingProgressProps) {
  const classes = useStyles();
  const dictionary = useDictionary();

  const slotList = Array.from(slots.values());
  const completed = slotList.filter((s) => s.status === 'success').length;
  const failed = slotList.filter((s) => s.status === 'error').length;
  const total = slotList.length;
  const isComplete = total > 0 && completed + failed === total;
  const isAllSuccess = isComplete && failed === 0;

  const title = isAllSuccess
    ? dictionary.bulkAllMessagesSent
    : isComplete
      ? dictionary.bulkSomeMessagesCouldNotBeSent
      : dictionary.sendingMessages;

  const subtitle = isComplete ? dictionary.checkRobinBoxForReplies : dictionary.robinJrBulkReachingOutToEachCandidate;

  return (
    <DialogV2.Root isVisible={true} customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        {isComplete && <DialogV2.Close onRequestClose={onClose} />}
        <DialogV2.Header customCss={classes.header}>
          <Send_01 size={24} color={(c) => c.primary.fuchsia[40]} />
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {title}
          </Typography>
          <div css={classes.headerDescription}>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[60]}>
              {subtitle}
            </Typography>
          </div>
          <BulkProgressBar
            completed={completed}
            failed={failed}
            total={total}
            isComplete={isComplete}
            labels={{
              inProgress: dictionary.bulkSending,
              allDone: `${dictionary.bulkAllMessagesSent} · ${dictionary.checkRobinBox}`,
              nFailed: dictionary.bulkNFailed,
              doneWithFailures: dictionary.bulkSendingDoneWithFailures,
            }}
          />
        </DialogV2.Header>

        <DialogV2.Body customCss={classes.body}>
          <div css={classes.list}>
            {slotList.map((slot, index) => (
              <div key={slot.esPersonId} css={index < slotList.length - 1 && classes.rowDivider}>
                <SendSlotRow slot={slot} onRetry={onRetry} />
              </div>
            ))}
          </div>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
}
